/**
 * batch-65 apk-rapport (FO-478) - het APK-keuringsrapport als documenttype met
 * voertuigkoppeling en vervaldatum-voorstel.
 *
 * Gedeeld contract tussen het upload-formulier (backoffice), de API (upload-
 * route, AI-voorinvulling, hertypeer-lijst) en de voertuig-/portal-weergave:
 *
 *  - `ApkRapportVeldenSchema`  - de rapport-velden. Alleen keuringsdatum en
 *    uitslag zijn verplicht; het formulier valideert er client-side mee en de
 *    API met exact hetzelfde schema (nooit een onterechte validatiefout).
 *  - `ApkRapportInvoerSchema`  - de velden plus de kenteken-bevestiging: noemt
 *    het rapport een ander kenteken dan het gekozen voertuig, dan is opslaan
 *    geblokkeerd tot de gebruiker bevestigt ("ik weet het zeker").
 *  - AI-voorinvulling: `ApkRapportExtractieDTO`. Lage zekerheid = veld leeg,
 *    nooit een gok (de API laat zulke waarden weg).
 *  - Waarschuwingen (vervaldatum buiten bereik, tweede rapport binnen 30 dagen,
 *    uitgefaseerd voertuig, kenteken wijkt af) rekent de pure helper
 *    `apkRapportWaarschuwingen` in @streep/utils uit; het type staat hier.
 *
 * De rapport-velden landen op de onderhoudslogregel van het voertuig (type
 * APK), naast het bestaande FO-329-APK-voorstel. Een goedgekeurd rapport met
 * een nieuwe vervaldatum wordt een VOORSTEL: nooit stil op het voertuig.
 */

import { z } from 'zod'
import type { GarageApkVoorstelStatus } from './garage-onderhoud'
import type { VoertuigStatus, VoertuigType } from './voertuigen'

// ─── Uitslag ─────────────────────────────────────────────────────────────────

export const APK_KEURING_UITSLAG = ['GOEDGEKEURD', 'AFGEKEURD'] as const
export type ApkKeuringUitslag = (typeof APK_KEURING_UITSLAG)[number]

export const APK_KEURING_UITSLAG_LABELS: Record<ApkKeuringUitslag, string> = {
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD: 'Afgekeurd',
}

// ─── Velden (gedeeld form/API) ───────────────────────────────────────────────

/** Vandaag (yyyy-mm-dd) in Europe/Amsterdam - de keuringsdatum mag niet later zijn. */
function vandaagIsoAmsterdam(nu: Date = new Date()): string {
  // en-CA formatteert als yyyy-mm-dd.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(nu)
}

/**
 * Kalender-geldige ISO-datum (yyyy-mm-dd). De regex alleen is niet genoeg:
 * "2026-02-31" rolt bij `new Date()` stil door naar 3 maart - de round-trip
 * weigert zulke waarden (zelfde vangrail als de groene-kaart-velden).
 */
export function isKalenderIsoDatum(t: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return false
  const d = new Date(`${t}T00:00:00.000Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === t
}

const isoDatum = (melding: string) =>
  z.string({ error: melding }).trim().refine(isKalenderIsoDatum, { message: melding })

export const ApkRapportVeldenSchema = z
  .object({
    /** Datum van de keuring (verplicht). */
    keuringsdatum: isoDatum('Vul de keuringsdatum in (dd-mm-jjjj)'),
    /** Uitslag van de keuring (verplicht). */
    uitslag: z.enum(APK_KEURING_UITSLAG, {
      error: 'Kies de uitslag: goedgekeurd of afgekeurd',
    }),
    /** Nieuwe APK-vervaldatum volgens het rapport (alleen bij goedgekeurd). */
    vervaldatum: isoDatum('Geen geldige vervaldatum (dd-mm-jjjj)').nullable(),
    /** Keuringsinstantie / garage die de keuring deed. */
    keuringsinstantie: z
      .string()
      .trim()
      .max(200, 'Maximaal 200 tekens')
      .nullable(),
    /** Kilometerstand bij de keuring. */
    kilometerstand: z
      .number({ error: 'Vul een geheel aantal kilometers in' })
      .int('Vul een geheel aantal kilometers in')
      .min(0, 'De kilometerstand kan niet negatief zijn')
      .max(9_999_999, 'Deze kilometerstand is niet realistisch')
      .nullable(),
    /** Rapport-/keuringsnummer. */
    rapportnummer: z.string().trim().max(100, 'Maximaal 100 tekens').nullable(),
  })
  .superRefine((v, ctx) => {
    if (isKalenderIsoDatum(v.keuringsdatum) && v.keuringsdatum > vandaagIsoAmsterdam()) {
      ctx.addIssue({
        code: 'custom',
        path: ['keuringsdatum'],
        message: 'De keuringsdatum ligt in de toekomst',
      })
    }
    if (v.uitslag === 'AFGEKEURD' && v.vervaldatum !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['vervaldatum'],
        message: 'Bij een afgekeurde keuring hoort geen nieuwe vervaldatum',
      })
    }
  })
export type ApkRapportVelden = z.infer<typeof ApkRapportVeldenSchema>

/** Lege velden (handmatige invoer / AI niet beschikbaar). */
export interface ApkRapportVeldenConcept {
  keuringsdatum: string | null
  uitslag: ApkKeuringUitslag | null
  vervaldatum: string | null
  keuringsinstantie: string | null
  kilometerstand: number | null
  rapportnummer: string | null
}

export const LEGE_APK_RAPPORT_VELDEN: ApkRapportVeldenConcept = {
  keuringsdatum: null,
  uitslag: null,
  vervaldatum: null,
  keuringsinstantie: null,
  kilometerstand: null,
  rapportnummer: null,
}

/**
 * Invoer bij opslaan (upload-veld `apkRapport` als JSON, of de hertypeer-body):
 * de rapport-velden + de kenteken-bevestiging. `kentekenRapport` is het kenteken
 * zoals het rapport het noemt (AI of handmatig), genormaliseerd of null.
 */
export const ApkRapportInvoerSchema = z.object({
  velden: ApkRapportVeldenSchema,
  kentekenRapport: z.string().trim().max(20).nullable().default(null),
  /** Bevestiging "ik weet het zeker" bij een afwijkend kenteken. */
  kentekenAfwijkingBevestigd: z.boolean().default(false),
})
export type ApkRapportInvoer = z.infer<typeof ApkRapportInvoerSchema>

/** Hertypeer-body: het bestaande document wordt een APK-rapport van dit voertuig. */
export const ApkHertypeerSchema = ApkRapportInvoerSchema.extend({
  voertuigId: z.string({ error: 'Kies het voertuig waar dit APK-rapport bij hoort' }).trim().min(1, {
    message: 'Kies het voertuig waar dit APK-rapport bij hoort',
  }),
})
export type ApkHertypeerInput = z.infer<typeof ApkHertypeerSchema>

/** Veldnamen voor de inline-melding (summarizeValidationIssues). */
export const APK_RAPPORT_VELD_LABELS: Record<string, string> = {
  'velden.keuringsdatum': 'Keuringsdatum',
  'velden.uitslag': 'Uitslag',
  'velden.vervaldatum': 'Nieuwe APK-vervaldatum',
  'velden.keuringsinstantie': 'Keuringsinstantie',
  'velden.kilometerstand': 'Kilometerstand',
  'velden.rapportnummer': 'Rapportnummer',
  voertuigId: 'Voertuig',
  kentekenAfwijkingBevestigd: 'Kenteken',
}

// ─── Waarschuwingen (§6) ─────────────────────────────────────────────────────

export const APK_RAPPORT_WAARSCHUWING_CODES = [
  'VERVALDATUM_VOOR_KEURING',
  'VERVALDATUM_TE_VER',
  'DUBBEL_BINNEN_30_DAGEN',
  'VOERTUIG_UITGEFASEERD',
  'KENTEKEN_AFWIJKING',
] as const
export type ApkRapportWaarschuwingCode = (typeof APK_RAPPORT_WAARSCHUWING_CODES)[number]

export interface ApkRapportWaarschuwing {
  code: ApkRapportWaarschuwingCode
  melding: string
  /**
   * true = opslaan wacht op een expliciete bevestiging (alleen de kenteken-
   * afwijking, zolang niet bevestigd). Al het andere is een waarschuwing.
   */
  blokkerend: boolean
}

// ─── Voertuig-keuze ──────────────────────────────────────────────────────────

/** Een voertuig in de APK-voertuigpicker (server-side zoeken op kenteken). */
export interface ApkVoertuigOptieDTO {
  id: string
  /** Weergavevorm (met streepjes). */
  kenteken: string
  merk: string
  model: string
  type: VoertuigType
  status: VoertuigStatus
  /** Verkocht of afgeschreven: koppelen mag, met melding (§6.3). */
  uitgefaseerd: boolean
}

/**
 * Ziet de bestuurder een APK-rapport met deze zichtbaarheid in de portal (Mijn
 * voertuig)? Alleen INTERN, via de voertuig-carve-out. GEKOPPELDE_MEDEWERKER
 * vraagt een medewerker-koppeling die een voertuigdocument niet heeft, en de
 * smallere keuzes (HR, finance, rollen, ...) houden het rapport buiten de portal.
 * Eén regel voor de upload-melding en de hertypeer-melding.
 */
export function apkRapportZichtbaarVoorBestuurder(zichtbaarheid: string): boolean {
  return zichtbaarheid === 'INTERN'
}

/** Melding bij een zichtbaarheid die het rapport voor de bestuurder verbergt. */
export const APK_RAPPORT_BESLOTEN_MELDING =
  'Met deze zichtbaarheid ziet de bestuurder het APK-rapport niet in de portal (Mijn voertuig).'

/**
 * Een APK-keuring van een voertuig zoals de actualiteit hem telt: een regel met
 * een APK-rapport, of een andere APK-regel met een echte datum (een garagefactuur
 * alleen met een gelezen werkdatum, niet de factuurdatum als terugval). Alleen
 * datums tot en met vandaag.
 */
export interface ApkKeuringMomentDTO {
  /** yyyy-mm-dd van de keuring. */
  datum: string
  /** Uitslag van het rapport; null = onbekend (garagefactuur of handmatige regel). */
  uitslag: ApkKeuringUitslag | null
  /** De regel noemt een nieuwe APK-vervaldatum (voorstel): de keuring is geslaagd. */
  metVervaldatum: boolean
}

/** Context van het gekozen voertuig voor de waarschuwingen in het formulier. */
export interface ApkVoertuigContextDTO {
  voertuig: ApkVoertuigOptieDTO
  /** Huidige APK-vervaldatum op het voertuig (yyyy-mm-dd) of null. */
  apkVervaldatum: string | null
  /** Eerder vastgelegde APK-rapporten (nieuwste eerst). */
  recenteRapporten: Array<{ keuringsdatum: string; uitslag: ApkKeuringUitslag | null }>
  /**
   * De APK-keuringen die de actualiteit telt (zelfde afleiding als de server),
   * zodat de voorvertoning hetzelfde zegt als de registratie.
   */
  keuringen: ApkKeuringMomentDTO[]
}

// ─── AI-voorinvulling ────────────────────────────────────────────────────────

export interface ApkRapportExtractieDTO {
  /** Voorgestelde velden; null = niet (zeker) gelezen - nooit een gok. */
  velden: ApkRapportVeldenConcept
  /** Kenteken zoals van het rapport gelezen (genormaliseerd), of null. */
  kenteken: string | null
  /**
   * Alleen bij een meegegeven voertuig: false = het rapport noemt een ANDER
   * kenteken (blokkerende melding tot bevestigd); null = geen kenteken gelezen.
   */
  kentekenMatch: boolean | null
  /** Eenduidig voertuig bij het gelezen kenteken (bibliotheek-upload), of null. */
  voertuigVoorstel: ApkVoertuigOptieDTO | null
  /** false = geen AI-voorstel (pauze/tegoed/onleesbaar); velden zijn dan leeg. */
  aiBeschikbaar: boolean
  /** Mensleesbare reden wanneer `aiBeschikbaar` false is. */
  reden: string | null
}

// ─── Registratie-uitkomst ────────────────────────────────────────────────────

/**
 * Wat er met de vervaldatum is gebeurd:
 *  - OPEN            voorstel staat klaar (bevestigen zet de APK-vervaldatum);
 *  - AL_ACTUEEL      het voertuig heeft deze vervaldatum al;
 *  - HISTORISCH      het voertuig heeft al een latere vervaldatum (ouder rapport);
 *  - NIET_VAN_TOEPASSING  afgekeurd, geen vervaldatum of uitgefaseerd voertuig.
 */
export const APK_VOORSTEL_UITKOMST = ['OPEN', 'AL_ACTUEEL', 'HISTORISCH', 'NIET_VAN_TOEPASSING'] as const
export type ApkVoorstelUitkomst = (typeof APK_VOORSTEL_UITKOMST)[number]

export interface ApkRapportRegistratieDTO {
  onderhoudId: string
  voorstel: ApkVoorstelUitkomst
  /** De APK-afspraak waaraan het rapport is gekoppeld, of null. */
  afspraak: { afspraakId: string; uitgevoerd: boolean } | null
  /** Niet-blokkerende meldingen (dubbel, bereik, uitgefaseerd, meerdere afspraken). */
  waarschuwingen: string[]
}

// ─── Weergave op het voertuig ────────────────────────────────────────────────

export interface VoertuigApkRapportDTO {
  onderhoudId: string
  /** Het rapport-document (download via de same-origin proxy), of null. */
  documentId: string | null
  /** Laatste versie van het document; null = geen (leesbare) versie. */
  versionNr: number | null
  titel: string | null
  keuringsdatum: string
  uitslag: ApkKeuringUitslag | null
  vervaldatum: string | null
  keuringsinstantie: string | null
  kilometerstand: number | null
  rapportnummer: string | null
  voorstelStatus: GarageApkVoorstelStatus | null
}

// ─── Hertypeer-lijst (§6.5) ──────────────────────────────────────────────────

export interface ApkHertypeerKandidaatDTO {
  documentId: string
  titel: string
  tags: string[]
  /** Huidig documenttype (null = geen). */
  documenttype: string | null
  scope: string
  versionNr: number | null
  fileName: string | null
  mimeType: string | null
  createdAt: string
  /** Voertuigen waaraan het document al gekoppeld is. */
  voertuigen: ApkVoertuigOptieDTO[]
  /** Voertuig waarvan het kenteken in de titel/bestandsnaam staat (voorstel). */
  voertuigVoorstel: ApkVoertuigOptieDTO | null
}
